import { upperFirst } from 'lodash';
import { environment } from '@environment';
import { IRole } from '@metutors/core/models';
import { ActivatedRoute } from '@angular/router';
import { CountryISO, PhoneNumberFormat } from 'ngx-intl-tel-input';
import { FormValidationUtilsService } from '@metutors/core/validators';
import { Affiliate, generalConstants, UserRole } from '@metutors/config';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormGroup,
  Validators,
  FormBuilder,
  AbstractControl,
} from '@angular/forms';

@Component({
  selector: 'metutors-signup-form',
  templateUrl: './signup-form.component.html',
  styleUrls: ['./signup-form.component.scss'],
})
export class SignupFormComponent implements OnInit {
  @Input() title: string;
  @Input() error: string;
  @Input() roles: IRole[];
  @Input() isModal = false;
  @Input() affiliate: string;
  @Input() returnUrl: string;
  @Input() isLoading: boolean;
  @Input() accountType: number;
  @Input() authLoading: boolean;
  @Input() submitLabel = 'CREATE_MY_ACCOUNT';

  @Output() register = new EventEmitter();
  @Output() changeStep = new EventEmitter();
  @Output() signinClicked = new EventEmitter();
  @Output() openRolesDialog = new EventEmitter();

  userRole = UserRole;
  signupForm: FormGroup;
  passwordVisibility = false;
  confirmPasswordVisibility = false;
  clientUrl = environment.clientUrl;
  PhoneNumberFormat = PhoneNumberFormat;
  signupError = generalConstants.signupError;
  onlyCountries: CountryISO[] = Object.keys(CountryISO)
    .filter(x => ![CountryISO.Israel].some(y => y == CountryISO[x]))
    .map(x => CountryISO[x]);

  constructor(
    private _fb: FormBuilder,
    private _route: ActivatedRoute,
    private _fv: FormValidationUtilsService
  ) {
    this.signupForm = this._fb.group(
      {
        firstName: [
          null,
          [
            Validators.required,
            Validators.minLength(1),
            Validators.maxLength(15),
          ],
        ],
        lastName: [
          null,
          [
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(15),
          ],
        ],
        email: [
          this._route.snapshot.queryParams['email'] || null,
          [
            Validators.required,
            Validators.email,
            Validators.pattern(
              /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            ),
          ],
        ],
        mobileNumber: [null, Validators.required],
        password: [
          null,
          [
            Validators.required,
            Validators.pattern(generalConstants.regex.password),
            this._fv.passwordValidation,
          ],
        ],
        confirmPassword: [null, Validators.required],
        conditions: [false, Validators.requiredTrue],
      },
      {
        validators: this._fv.controlsMismatchValidator(
          'password',
          'confirmPassword'
        ),
      }
    );
  }

  ngOnInit(): void {
    if (this._route.snapshot.queryParams['email']) {
      this.email?.markAsTouched();
      this.email?.updateValueAndValidity();
    }
  }

  get firstName(): AbstractControl | null {
    return this.signupForm.get('firstName');
  }

  get lastName(): AbstractControl | null {
    return this.signupForm.get('lastName');
  }

  get email(): AbstractControl | null {
    return this.signupForm.get('email');
  }

  get password(): AbstractControl | null {
    return this.signupForm.get('password');
  }

  get confirmPassword(): AbstractControl | null {
    return this.signupForm.get('confirmPassword');
  }

  get phoneNumber(): AbstractControl | null {
    return this.signupForm.get('mobileNumber');
  }

  onSubmit(form: FormGroup, userType: number): void {
    if (form.invalid) {
      return;
    }

    const user: any = {
      first_name: upperFirst(this.firstName?.value),
      last_name: upperFirst(this.lastName?.value),
      mobile: this.phoneNumber?.value?.e164Number,
      email: this.email?.value,
      password: this.password?.value,
      confirm_password: this.confirmPassword?.value,
      country_code: this.phoneNumber?.value?.dialCode,
      role: userType,
      affiliate: this.affiliate || Affiliate.normal,
    };

    this.register.emit(user);
  }
}
